// Hotlist des factures impayées (Top 5)

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  useTheme,
  Divider,
} from '@mui/material';
import { WhatsApp, Warning } from '@mui/icons-material';
import { FactureImpayee } from '../../types/dashboard';

interface HotlistImpayesProps {
  factures: FactureImpayee[];
}

export const HotlistImpayes: React.FC<HotlistImpayesProps> = ({ factures }) => {
  const theme = useTheme();

  const handleWhatsApp = (telephone: string, facture: FactureImpayee) => {
    const montant = facture.montantTTC ? facture.montantTTC.toLocaleString('fr-FR') : '0';
    const message = encodeURIComponent(
      `Bonjour, je vous contacte concernant la facture ${facture.numeroFacture} d'un montant de ${montant}€ TTC, échue depuis le ${new Date(facture.dateEcheance).toLocaleDateString('fr-FR')}.`
    );
    const url = `https://wa.me/${telephone.replace(/\s/g, '')}?text=${message}`;
    window.open(url, '_blank');
  };

  const getGraviteCouleur = (joursRetard: number) => {
    if (joursRetard >= 30) return theme.palette.error.main;
    if (joursRetard >= 15) return theme.palette.warning.main;
    return theme.palette.info.main;
  };

  if (factures.length === 0) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Warning sx={{ color: theme.palette.success.main }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Impayés
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            Aucune facture impayée. Excellent travail !
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={2}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Warning sx={{ color: theme.palette.error.main }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Impayés - Top 5
          </Typography>
          <Chip
            label={factures.length}
            size="small"
            color="error"
            sx={{ ml: 'auto' }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {factures.map((facture, index) => (
            <Box
              key={facture.id}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: '#F9FAFB',
                border: '1px solid #E5E7EB',
                position: 'relative',
                '&:hover': {
                  bgcolor: '#F3F4F6',
                  borderColor: '#D1D5DB',
                },
              }}
            >
              {/* En-tête : Numéro + Badge retard */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#374151' }}>
                    {facture.numeroFacture}
                  </Typography>
                  <Chip
                    label={`${facture.joursRetard}j de retard`}
                    size="small"
                    sx={{
                      backgroundColor: `${getGraviteCouleur(facture.joursRetard)}20`,
                      color: getGraviteCouleur(facture.joursRetard),
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 22,
                    }}
                  />
                </Box>
                {facture.clientTelephone && (
                  <IconButton
                    size="small"
                    onClick={() => handleWhatsApp(facture.clientTelephone!, facture)}
                    sx={{
                      color: '#25D366',
                      bgcolor: '#25D36610',
                      '&:hover': {
                        bgcolor: '#25D36620',
                      },
                    }}
                  >
                    <WhatsApp fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {/* Informations client */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.5 }}>
                  {facture.clientNom}
                </Typography>
                {facture.adresse && (
                  <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', lineHeight: 1.4 }}>
                    📍 {facture.adresse}
                  </Typography>
                )}
              </Box>

              {/* Montant et échéance */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                pt: 1.5,
                borderTop: '1px solid #E5E7EB',
              }}>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  Échéance: <strong>{new Date(facture.dateEcheance).toLocaleDateString('fr-FR')}</strong>
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#EF4444',
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  }}
                >
                  {facture.montantTTC ? facture.montantTTC.toLocaleString('fr-FR') : '0'} €
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};
